// src/components/password-baul/hooks/useAddCardV.ts
import { useState, useRef, useEffect } from "react";
import { ISecretDTO } from "../interfaces/ISecretDTO";

export interface RegisterSecretModalProps {
  userCompanyId: number;
  onSave: (dto: ISecretDTO, imageFile: File | null) => Promise<void>;
  remainingSeconds?: number;
  onAfterSave?: () => void;
}

type FieldErrors = Partial<Record<"title" | "usernameEmail" | "password" | "websiteUrl" | "notes", string>>;
type Touched = Partial<Record<keyof FieldErrors, boolean>>;

const normalizeWebsiteToNull = (v: string) => {
  const t = (v ?? "").trim();
  return t === "" ? null : t;
};

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const isValidUrl = (v: string) => {
  try {
    const u = new URL(v);
    return Boolean(u.protocol && u.host);
  } catch {
    return false;
  }
};

const validateAll = (vals: { title: string; userEmail: string; password: string; website: string; notes: string }): FieldErrors => {
  const errs: FieldErrors = {};

  const t = (vals.title ?? "").trim();
  if (t.length < 1) errs.title = "Title is required";

  const ue = (vals.userEmail ?? "").trim();
  if (ue.length < 3) {
    errs.usernameEmail = "Must be a valid email or a username with at least 3 characters.";
  } else if (ue.includes("@") && !isValidEmail(ue)) {
    errs.usernameEmail = "Must be a valid email or a username with at least 3 characters.";
  }

  const pw = vals.password ?? "";
  if (pw.length < 8) errs.password = "Password must be at least 8 characters long.";

  const web = normalizeWebsiteToNull(vals.website ?? "");
  if (web !== null && !isValidUrl(web)) {
    errs.websiteUrl = "Invalid URL format (e.g., https://example.com)";
  }

  return errs;
};

export const useAddCardV = ({ onSave, onAfterSave }: RegisterSecretModalProps) => {
  const [title, setTitle] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [password, setPassword] = useState("");
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [highSecurity, setHighSecurity] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Touched>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const touchField = (key: keyof FieldErrors) => setTouched((t) => ({ ...t, [key]: true }));
  const showFieldError = (key: keyof FieldErrors) => Boolean(touched[key] && fieldErrors[key]);

  const runValidation = (
    next?: Partial<{
      title: string;
      userEmail: string;
      password: string;
      website: string;
      notes: string;
    }>,
  ) => {
    const errs = validateAll({
      title: next?.title ?? title,
      userEmail: next?.userEmail ?? userEmail,
      password: next?.password ?? password,
      website: next?.website ?? website,
      notes: next?.notes ?? notes,
    });
    setFieldErrors(errs);
    return errs;
  };

  const hasErrors = (errs: FieldErrors) => Object.keys(errs).length > 0;

  const resetForm = () => {
    setTitle("");
    setUserEmail("");
    setPassword("");
    setWebsite("");
    setNotes("");
    setHighSecurity(false);
    setShowPassword(false);

    setAvatarFile(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);

    if (fileInputRef.current) fileInputRef.current.value = "";

    setSaving(false);
    setError(null);
    setFieldErrors({});
    setTouched({});
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);

    setAvatarFile(file);
    setAvatarPreview(url);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);
    setAvatarFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const canSave = !saving && !hasErrors(validateAll({ title, userEmail, password, website, notes }));

  const handleSave = async () => {
    if (saving) return;

    setTouched({
      title: true,
      usernameEmail: true,
      password: true,
      websiteUrl: true,
      notes: true,
    });

    const errs = runValidation();
    if (hasErrors(errs)) return;

    setSaving(true);
    setError(null);

    const newSecret: ISecretDTO = {
      title: title.trim(),
      usernameEmail: userEmail.trim(),
      password,
      websiteUrl: normalizeWebsiteToNull(website) as any,
      notes: notes.trim(),
      highSecurity,
      imageUrl: "",
    };

    try {
      await onSave(newSecret, avatarFile);
      resetForm();
      onAfterSave?.(); 
    } catch (e: any) {
      console.error("Save Secret error:", e?.response?.data ?? e);
      setError(e?.response?.data?.message ?? "Failed to save secret");
    } finally {
      setSaving(false);
    }
  };

  return {
    title,
    userEmail,
    password,
    website,
    notes,
    highSecurity,
    showPassword,

    setTitle,
    setUserEmail,
    setPassword,
    setWebsite,
    setNotes,
    setHighSecurity,
    setShowPassword,

    avatarPreview,
    avatarFile,
    fileInputRef,
    handleImageChange,
    removeImage,

    fieldErrors,
    touchField,
    runValidation,
    showFieldError,

    saving,
    error,
    canSave,
    resetForm,
    handleSave,
  };
};
